"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SubscriptionPill — the ONE subscription chip of the fleet sidebar, beside the profile cluster.
 * Two faces, one look:
 *   • allowance — a live "%" of the monthly allowance (stories / film): seeded from the server,
 *     re-fetched from `refreshUrl` on an interval + window focus; turns red when low.
 *   • plan      — the plan tier (habits): a crown for a paid tier, "Upgrade" / "Manage" hint.
 * A tap opens the in-app <SubscribeWidget> (openSubscribe) when it is mounted; otherwise it
 * navigates to `fallbackHref` (accounts /billing).
 */
import { useEffect, useState } from "react";
import { openSubscribe, isSubscribeWidgetMounted } from "./SubscribeWidget";
const CHIP = "inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold transition-colors";
const NEUTRAL = "border-border bg-muted/50 text-foreground hover:bg-muted";
const LOW = "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20";
function open(appId, fallbackHref) {
    if (isSubscribeWidgetMounted())
        openSubscribe(appId);
    else if (typeof window !== "undefined")
        window.location.href = fallbackHref;
}
export function SubscriptionPill(props) {
    if (props.kind === "allowance")
        return _jsx(AllowancePill, { ...props });
    const { appId, tierLabel, isTop, isFree, upgradeLabel, manageLabel, label, fallbackHref, forced } = props;
    return (_jsxs("button", { type: "button", onClick: () => open(appId, fallbackHref), title: label, "aria-label": `${label}: ${tierLabel}`, "data-testid": "sidebar-plan", className: `${CHIP} ${NEUTRAL}`, children: [!isFree && _jsx("span", { "aria-hidden": true, children: "\uD83D\uDC51" }), _jsx("span", { className: "truncate", children: tierLabel }), forced && _jsx("span", { className: "rounded bg-warning/20 px-1 text-[10px] font-bold uppercase text-warning-foreground", title: "dev", children: "dev" }), !isTop && _jsx("span", { className: "text-primary", children: isFree ? upgradeLabel : manageLabel })] }));
}
function AllowancePill({ appId, pct: initialPct, low: initialLow, label, fallbackHref, refreshUrl }) {
    const [pct, setPct] = useState(initialPct);
    const [low, setLow] = useState(initialLow);
    useEffect(() => {
        if (!refreshUrl)
            return;
        let alive = true;
        const load = async () => {
            try {
                const r = await fetch(refreshUrl, { cache: "no-store" });
                if (!r.ok)
                    return;
                const d = (await r.json());
                if (alive) {
                    setPct(d.pct);
                    setLow(!!d.low);
                }
            }
            catch { /* transient — keep the last value */ }
        };
        const iv = setInterval(load, 20_000);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
    }, [refreshUrl]);
    if (pct == null)
        return null;
    return (_jsxs("button", { type: "button", onClick: () => open(appId, fallbackHref), title: label, "aria-label": `${label}: ${pct}%`, "data-testid": "sidebar-allowance", className: `${CHIP} ${low ? LOW : NEUTRAL}`, children: [_jsx("span", { "aria-hidden": true, className: `h-1.5 w-1.5 rounded-full ${low ? "bg-destructive" : "bg-success"}` }), pct, "%"] }));
}
